// src/types/events.ts
import { GTMConfig } from '@/tracking/gtm/config/types';

// Event names
export type EventName = 
  // System Events
  | '0.01_UNIFIED_BASE_TAG'
  | '0.02_EVENT_DISTRIBUTOR'
  | '0.03_CAPI_HANDLER_TAG'
  | '0.04_VSL_CORE_EVENTS'
  | '0.05_CONVERSION_TRACKING_SYSTEM'
  | '0.06_SURVEY_TRACKING_SYSTEM'
  // Video Events
  | 'VIDEO_START' 
  | 'VIDEO_PROGRESS' 
  | 'VIDEO_COMPLETE'
  | 'VIDEO_MILESTONE'
  // Survey Events
  | 'SURVEY_PROGRESS'
  | 'SURVEY_QUESTION_ANSWER'
  | 'SURVEY_QUALIFICATION_STATUS'
  | 'SURVEY_START'
  | 'SURVEY_COMPLETE'
  // Conversion Events
  | 'APPOINTMENT_BOOKED'
  | 'DOWNLOAD_CONTENT'
  | 'JOIN_COMMUNITY'
  | 'Lead'
  // Page Events
  | 'app_init'
  | 'PageView'
  | 'ViewContent';

// Event mapping for GA4 and other platforms
export const EVENT_MAPPING: Record<EventName, { ga4: string; fb: string }> = {
  // System Events
  '0.01_UNIFIED_BASE_TAG': { ga4: 'system_init', fb: 'system_init' },
  '0.02_EVENT_DISTRIBUTOR': { ga4: 'event_distributor', fb: 'event_distributor' },
  '0.03_CAPI_HANDLER_TAG': { ga4: 'capi_handler', fb: 'capi_handler' },
  '0.04_VSL_CORE_EVENTS': { ga4: 'vsl_core', fb: 'vsl_core' },
  '0.05_CONVERSION_TRACKING_SYSTEM': { ga4: 'conversion_system', fb: 'conversion_system' },
  '0.06_SURVEY_TRACKING_SYSTEM': { ga4: 'survey_system', fb: 'survey_system' },

  // Video Events
  'VIDEO_START': { ga4: 'video_start', fb: 'ViewContent' },
  'VIDEO_PROGRESS': { ga4: 'video_progress', fb: 'ViewContent' },
  'VIDEO_COMPLETE': { ga4: 'video_complete', fb: 'ViewContent' },
  'VIDEO_MILESTONE': { ga4: 'video_milestone', fb: 'ViewContent' },

  // Survey Events
  'SURVEY_PROGRESS': { ga4: 'survey_progress', fb: 'InitiateCheckout' },
  'SURVEY_QUESTION_ANSWER': { ga4: 'survey_interaction', fb: 'InitiateCheckout' },
  'SURVEY_QUALIFICATION_STATUS': { ga4: 'generate_lead', fb: 'Subscribe' },
  'SURVEY_START': { ga4: 'begin_survey', fb: 'InitiateCheckout' },
  'SURVEY_COMPLETE': { ga4: 'complete_survey', fb: 'InitiateCheckout' },

  // Conversion Events
  'APPOINTMENT_BOOKED': { ga4: 'schedule_appointment', fb: 'Schedule' },
  'DOWNLOAD_CONTENT': { ga4: 'file_download', fb: 'Download' },
  'JOIN_COMMUNITY': { ga4: 'generate_lead', fb: 'Lead' },
  'Lead': { ga4: 'generate_lead', fb: 'Lead' },

  // Page Events
  'app_init': { ga4: 'app_init', fb: 'app_init' },
  'PageView': { ga4: 'page_view', fb: 'PageView' },
  'ViewContent': { ga4: 'view_content', fb: 'ViewContent' }
};

export type PageType = 'video' | 'survey' | 'conversion' | 'standard' | 'vsl' | 'app_init' | 'error';

// Base event parameters
export interface BaseEventParams {
  content_type: string;
  page_type: PageType;
  funnel_path: string;
  event_id: string;
  timestamp: string;
  event_name?: string;
  page_source?: string;
  page_url?: string;
  content_name?: string;
  [key: string]: unknown;
}

// Video-specific parameters
export interface VideoEventParams extends BaseEventParams {
  video_id: string;
  video_title: string;
  video_duration: number;
  video_percent: number;
  video_status: 'started' | 'progress' | 'completed';
  cf_stream_id?: string;
  cf_player_version?: string;
  cf_quality?: string;
  engagement_time_msec?: number;
  milestone?: number;
  video_progress?: number;
}

// Survey-specific parameters
export interface SurveyEventParams extends BaseEventParams {
  survey_id: string;
  survey_step: number;
  survey_answer: string;
  survey_version: string;
  survey_question_id?: string;
  survey_question_type?: string;
  survey_funnel_progression?: number;
}

// Appointment-specific parameters
export interface AppointmentEventParams extends BaseEventParams {
  appointment_id: string;
  ghl_id: string;
  appointment_timezone: string;
  appointment_start_date: string;
  appointment_start_time: string;
}

// User parameters
export interface UserParams {
  user_id?: string;
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  country?: string;
}

export type EventParams = BaseEventParams | VideoEventParams | SurveyEventParams | AppointmentEventParams;

export interface TrackingEvent {
  event: EventName;
  detail?: {
    eventParams: EventParams;
    userParams?: UserParams;
  };
}

export interface EventConfig {
  GTM_CONFIG: GTMConfig;
  MAPPING: typeof EVENT_MAPPING;
  normalize: (eventName: EventName) => { ga4: string; fb: string };
}

declare global {
  interface Window {
    EVENT_CONFIG: EventConfig;
    vslEvents: {
      handleVideoEvent: (action: string, progress: number) => void;
      handleSurveyStart: () => void;
    };
  }
}
